import React, { useEffect, useState } from "react";
import { AuditTotalPercent, QaTimeCardResponseDetail, RejectionTotalPercent } from "interfaces/Mueller.Business.Models";
import { MGrid, MSpinner } from "components/Shared";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { DATA_TYPE_BUTTON, DATA_TYPE_NUMBER_HUNDREDTHS, DATA_TYPE_PERCENT_DECIMAL, DATA_TYPE_STRING } from "typeConstants/dataTypes";

interface TimeLogSummaryGridProps {
  summaryData: QaTimeCardResponseDetail,
  onCellButtonClick: ((selectedOriginalIndex: number, columnKey: string) => void) | (() => void),
  className?: string,
}

const noAuditDataText = "-- --";

// Override all changed properties
interface FormattedQaTimeCardResponseDetail extends Omit<QaTimeCardResponseDetail, 
  'costDifference' | 'totalExpectedMinutes' | 'auditPercentage' | 'auditPercentage30' | 'rejectionPercentage' | 'rejectionAuditPercentage' | 'rejectionAuditPercentage30'
> {
  costDifference: string;
  totalExpectedMinutes: number | undefined;
  auditPercentage: { amount: number | string; passedCount?: number; totalCount?: number };
  auditPercentage30: { amount: number | string; passedCount?: number; totalCount?: number };
  rejectionPercentage: { amount: number | string; rejectionCount?: number; totalCount?: number };
  rejectionAuditPercentage: { amount: number | string; passedCount?: number; totalCount?: number };
  rejectionAuditPercentage30: { amount: number | string; passedCount?: number; totalCount?: number };
}

const TimeLogSummaryGrid = (props: TimeLogSummaryGridProps) => {

  const [formattedData, setFormattedData] = useState<FormattedQaTimeCardResponseDetail>();

  const formatAuditAmount = (obj: AuditTotalPercent): string | null => {
    if (obj.passedCount === 0 && obj.totalCount === 0)
      return noAuditDataText;
    //return null if we're not returning the noAuditDataText, if it's null then it'll just use whatever is in props later on
    return null;
  };

  useEffect(() => {
    let formattedTotalExpectedMinutes = props.summaryData?.totalExpectedMinutes / 60.00;
    let auditPercentageAmount: number | string = formatAuditAmount(props.summaryData.auditPercentage);
    let auditPercentage30Amount: number | string = formatAuditAmount(props.summaryData.auditPercentage30);
    let rejectionPercentageAmount: number | string = props.summaryData.rejectionPercentage.rejectionCount === 0 && props.summaryData.rejectionPercentage.totalCount === 0 ? noAuditDataText : null;
    let rejectionAuditPercentageAmount: number | string = formatAuditAmount(props.summaryData.rejectionAuditPercentage);
    let rejectionAuditPercentage30Amount: number | string = formatAuditAmount(props.summaryData.rejectionAuditPercentage30);
    
    setFormattedData(
      { 
        ...props.summaryData,
        totalExpectedMinutes: formattedTotalExpectedMinutes,
        costDifference: props.summaryData.costDifference.toFixed(2),
        auditPercentage: {
          amount: auditPercentageAmount ?? props.summaryData.auditPercentage.amount,
          passedCount: props.summaryData.auditPercentage.passedCount,
          totalCount: props.summaryData.auditPercentage.totalCount,
        },
        auditPercentage30: {
          amount: auditPercentage30Amount ?? props.summaryData.auditPercentage30.amount,
          passedCount: props.summaryData.auditPercentage30.passedCount,
          totalCount: props.summaryData.auditPercentage30.totalCount,
        },
        rejectionPercentage: {
          amount: rejectionPercentageAmount ?? props.summaryData.rejectionPercentage.amount,
          rejectionCount: props.summaryData.rejectionPercentage.rejectionCount,
          totalCount: props.summaryData.rejectionPercentage.totalCount,
        },
        rejectionAuditPercentage: {
          amount: rejectionAuditPercentageAmount ?? props.summaryData.rejectionAuditPercentage.amount,
          passedCount: props.summaryData.rejectionAuditPercentage.passedCount,
          totalCount: props.summaryData.rejectionAuditPercentage.totalCount,
        },
        rejectionAuditPercentage30: {
          amount: rejectionAuditPercentage30Amount ?? props.summaryData.rejectionAuditPercentage30.amount,
          passedCount: props.summaryData.rejectionAuditPercentage30.passedCount,
          totalCount: props.summaryData.rejectionAuditPercentage30.totalCount,
        },
      }
    );
  }, [props.summaryData]);

  if (!formattedData)
    return <MSpinner />;

  const getGridColumns = (): MGridColumnDefinition[] => {
    const viewButton = () => <i className="md-icon material-icons qa-dashboard-view-button">search</i>;
    const auditPercentageAmountTypeIsNumber = typeof(formattedData.auditPercentage.amount) === "number";
    const auditPercengate30AmountTypeIsNumber = typeof(formattedData.auditPercentage30.amount) === "number";
    const rejectionPercentageAmountTypeIsNumber = typeof(formattedData.rejectionPercentage.amount) === "number";
    const rejectionAuditPercentageAmountTypeIsNumber = typeof(formattedData.rejectionAuditPercentage.amount) === "number";
    const rejectionAuditPercentage30AmountTypeIsNumber = typeof(formattedData.rejectionAuditPercentage30.amount) === "number";

    return [
      {
        key: "time-log-summary-employee-name",
        dataSource: "employeeName.firstLast",
        name: "Employee Name",
        width: 240,
      },
      {
        key: "time-log-summary-clock-in-hours",
        dataSource: "payrollClockInTimeHours",
        name: "Clocked In Hrs",
        width: 120,
        dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      },
      {
        key: "time-log-summary-expected-hours",
        dataSource: "totalExpectedMinutes",
        name: "Expected Hrs",
        width: 120,
        dataType: DATA_TYPE_NUMBER_HUNDREDTHS,
      },
      {
        key: "time-log-summary-cost-diff",
        dataSource: "costDifference",
        name: "Cost Diff.",
        width: 80,
        dataType: DATA_TYPE_STRING,
      },
      {
        key: "time-log-summary-cost-efficiency",
        dataSource: "costEfficiency",
        name: "Cost Eff %",
        width: 80,
        dataType: DATA_TYPE_PERCENT_DECIMAL,
      },
      {
        key: "time-log-summary-time-efficiency",
        dataSource: "timeEfficiency",
        name: "Time Eff %",
        width: 100,
        dataType: DATA_TYPE_PERCENT_DECIMAL,
      },
      {
        key: "time-log-summary-audit-7",
        dataSource: "auditPercentage.amount",
        name: "Audit 7",
        width: 80,
        dataType: auditPercentageAmountTypeIsNumber ? DATA_TYPE_PERCENT_DECIMAL : null,
        tooltip: () => `${formattedData.auditPercentage.passedCount} Passed / ${formattedData.auditPercentage.totalCount} Total`,
      },
      {
        key: "time-log-summary-audit-30",
        dataSource: "auditPercentage30.amount",
        name: "Audit 30",
        width: 80,
        dataType: auditPercengate30AmountTypeIsNumber ? DATA_TYPE_PERCENT_DECIMAL : null,
        tooltip: () => `${formattedData.auditPercentage30.passedCount} Passed / ${formattedData.auditPercentage30.totalCount} Total`,
      },
      {
        key: "time-log-summary-rejection-percentage",
        dataSource: "rejectionPercentage.amount",
        name: "Rej",
        width: 80,
        dataType: rejectionPercentageAmountTypeIsNumber ? DATA_TYPE_PERCENT_DECIMAL : null,
        tooltip: () => `${formattedData.rejectionPercentage.rejectionCount} Rejected / ${formattedData.rejectionPercentage.totalCount} Total`,
      },
      {
        key: "time-log-summary-rejection-audit-percentage",
        dataSource: "rejectionAuditPercentage.amount",
        name: "Rej Q 7",
        width: 80,
        dataType: rejectionAuditPercentageAmountTypeIsNumber ? DATA_TYPE_PERCENT_DECIMAL : null,
        tooltip: () => `${formattedData.rejectionAuditPercentage.passedCount} Passed / ${formattedData.rejectionAuditPercentage.totalCount} Total`,
      },
      {
        key: "time-log-summary-rejection-audit-percentage-30",
        dataSource: "rejectionAuditPercentage30.amount",
        name: "Rej Q 30",
        width: 80,
        dataType: rejectionAuditPercentage30AmountTypeIsNumber ? DATA_TYPE_PERCENT_DECIMAL : null,
        tooltip: () => `${formattedData.rejectionAuditPercentage30.passedCount} Passed / ${formattedData.rejectionAuditPercentage30.totalCount} Total`,
      },
      {
        key: "time-log-summary-view-details-button",
        name: "View",
        dataSource: viewButton,
        dataType: DATA_TYPE_BUTTON,
        tooltip: "Click to view details",
        width: 80,
      }
    ];
  };

  return (
    <MGrid
      className={props.className ?? "time-log-summary-card"}
      data={[formattedData]}
      dataLastChanged={Date.now()}
      columnDefinitions={getGridColumns()}
      onCellButtonClick={props.onCellButtonClick}
      cardStyle
      disableFilters
      headerText="Time Log Summary"
    />
  );
};

export default TimeLogSummaryGrid;